import { useEffect, useState } from 'react';
import { eventBus } from '@/infra/event-bus/EventBus';
import { useT } from '@/infra/i18n';
import type { TranslationKey } from '@/infra/i18n/types';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';

interface ToastItem {
  id: number;
  key: TranslationKey;
  severity: 'info' | 'warn' | 'error' | 'ok';
  params?: Record<string, string> | undefined;
}

let nextId = 1;

export function ToastQueue() {
  const t = useT();
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const unsub = eventBus.on('notification.show', (p) => {
      const id = nextId++;
      setItems((cur) => [...cur, {
        id,
        key: p.key,
        severity: p.severity ?? 'info',
        params: p.params,
      }]);
      setTimeout(() => {
        setItems((cur) => cur.filter((x) => x.id !== id));
      }, 3000);
    });
    return unsub;
  }, []);

  if (items.length === 0) return null;
  return (
    <div style={container} role="region" aria-label="Notificações">
      {items.map((it) => (
        <div
          key={it.id}
          role="status"
          style={{
            ...toast,
            borderColor: severityColor(it.severity),
            boxShadow: `0 0 0 1px #0a0a14, 4px 4px 0 ${severityColor(it.severity)}33`,
          }}
        >
          <span style={{ color: severityColor(it.severity), fontFamily: FONT.mono, fontWeight: 700 }}>
            {severityIcon(it.severity)}
          </span>
          <span style={{ color: COLORS.text, fontFamily: FONT.family, fontSize: FONT.sizeSm }}>
            {t(it.key, it.params)}
          </span>
        </div>
      ))}
    </div>
  );
}

function severityColor(s: ToastItem['severity']): string {
  switch (s) {
    case 'error': return '#ff5566';
    case 'warn': return '#ffcc66';
    case 'ok': return '#6cf08f';
    default: return '#5aafff';
  }
}
function severityIcon(s: ToastItem['severity']): string {
  switch (s) {
    case 'error': return '✗';
    case 'warn': return '!';
    case 'ok': return '✓';
    default: return 'i';
  }
}

const container: React.CSSProperties = {
  position: 'fixed',
  top: 60,
  right: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.sm,
  zIndex: 1000,
  maxWidth: 320,
  pointerEvents: 'none',
};
const toast: React.CSSProperties = {
  background: '#0e0e1c',
  border: '3px solid',
  borderRadius: RADIUS.md,
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.sm,
  fontFamily: '"Courier New", monospace',
  imageRendering: 'pixelated',
  pointerEvents: 'auto',
  animation: 'slideIn 0.3s steps(4)',
};
